"use client"

import { useEffect, useState, useCallback } from "react"
import { useNavigate } from "react-router-dom"
import { Container, Card, Form, Button, Table, Alert, ProgressBar } from "react-bootstrap"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import {
  fetchUserGuests,
  registerGuest,
  fetchUserMonthlySummary,
  checkMonthlyLimit,
  clearLimitCheck,
} from "../../redux/slices/guestsSlice"
import { useTranslation } from "../../hooks/use-translation"
import { useToast } from "../../components/ui/use-toast"
import { MessageSquare } from "lucide-react"

export default function GuestRegistrationPage() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { guests, historyDisabled, monthlySummary, limitCheck, isLoading } = useAppSelector(
    (state) => state.guests,
  )
  const { t } = useTranslation()
  const { toast } = useToast()

  const [isParents, setIsParents] = useState(false)
  const [formData, setFormData] = useState({
    fullName: "",
    idNumber: "",
    checkInDate: "",
    checkOutDate: "",
    notes: "",
  })

  useEffect(() => {
    dispatch(fetchUserGuests())
    dispatch(fetchUserMonthlySummary())
  }, [dispatch])

  // Debounced limit check when dates change
  useEffect(() => {
    if (formData.checkInDate && formData.checkOutDate && formData.checkOutDate > formData.checkInDate) {
      const timer = setTimeout(() => {
        dispatch(checkMonthlyLimit({ checkInDate: formData.checkInDate, checkOutDate: formData.checkOutDate }))
      }, 500)
      return () => clearTimeout(timer)
    } else {
      dispatch(clearLimitCheck())
    }
  }, [formData.checkInDate, formData.checkOutDate, dispatch])

  const calculatedDays =
    formData.checkInDate && formData.checkOutDate && formData.checkOutDate > formData.checkInDate
      ? Math.ceil(
          (new Date(formData.checkOutDate).getTime() - new Date(formData.checkInDate).getTime()) /
            (1000 * 60 * 60 * 24),
        )
      : 0

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()

      if (!formData.fullName || !formData.idNumber || !formData.checkInDate || !formData.checkOutDate) {
        toast({ title: t("error"), description: t("allFieldsRequired"), variant: "destructive" })
        return
      }

      if (formData.checkOutDate <= formData.checkInDate) {
        toast({ title: t("error"), description: t("checkOutAfterCheckIn"), variant: "destructive" })
        return
      }

      try {
        const submitData = formData.notes
          ? formData
          : { fullName: formData.fullName, idNumber: formData.idNumber, checkInDate: formData.checkInDate, checkOutDate: formData.checkOutDate }
        await dispatch(registerGuest(submitData)).unwrap()
        toast({ title: t("success"), description: t("guestRegisteredSuccessfully") })
        setIsParents(false)
        setFormData({ fullName: "", idNumber: "", checkInDate: "", checkOutDate: "", notes: "" })
        dispatch(fetchUserMonthlySummary())
        dispatch(clearLimitCheck())
      } catch (error: any) {
        toast({ title: t("error"), description: error || t("guestRegistrationFailed"), variant: "destructive" })
      }
    },
    [formData, dispatch, toast, t],
  )

  const today = new Date().toISOString().split("T")[0]
  const isCheckInPast = formData.checkInDate && formData.checkInDate < today
  const isCheckOutInvalid = formData.checkInDate && formData.checkOutDate && formData.checkOutDate <= formData.checkInDate

  const limitPercentage =
    monthlySummary && monthlySummary.monthlyLimit > 0
      ? Math.min(100, Math.round((monthlySummary.totalDaysThisMonth / monthlySummary.monthlyLimit) * 100))
      : 0

  return (
    <Container fluid className="py-4 px-2 px-md-4">
      <h1 className="fs-2 fw-bold mb-4 text-white">{t("guestRegistration")}</h1>

      {/* Registration Form */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body className="p-4">
          <h5 className="fw-semibold mb-3">{t("registerGuest")}</h5>
          <Form onSubmit={handleSubmit}>
            <Form.Check
              type="switch"
              id="parents-toggle"
              label={t("parents")}
              checked={isParents}
              onChange={(e) => {
                const checked = e.target.checked
                setIsParents(checked)
                if (checked) {
                  setFormData({ ...formData, fullName: "Γονείς", idNumber: "-" })
                } else {
                  setFormData({ ...formData, fullName: "", idNumber: "" })
                }
              }}
              className="mb-3"
            />
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t("guestFullName")}</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    placeholder={t("guestFullName")}
                    disabled={isParents}
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>{t("guestIdNumber")}</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.idNumber}
                    onChange={(e) => setFormData({ ...formData, idNumber: e.target.value })}
                    placeholder={t("guestIdNumber")}
                    disabled={isParents}
                    required
                  />
                </Form.Group>
              </div>
            </div>
            <div className="row">
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>{t("checkInDate")}</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.checkInDate}
                    onChange={(e) => setFormData({ ...formData, checkInDate: e.target.value })}
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group className="mb-3">
                  <Form.Label>{t("checkOutDate")}</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.checkOutDate}
                    onChange={(e) => setFormData({ ...formData, checkOutDate: e.target.value })}
                    min={formData.checkInDate || undefined}
                    isInvalid={!!isCheckOutInvalid}
                    required
                  />
                  {isCheckOutInvalid && (
                    <Form.Control.Feedback type="invalid">
                      {t("checkOutAfterCheckIn")}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </div>
              <div className="col-md-4 d-flex align-items-end">
                <Form.Group className="mb-3 w-100">
                  <Form.Label>{t("totalDays")}</Form.Label>
                  <Form.Control type="text" value={calculatedDays > 0 ? calculatedDays : "-"} disabled />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-3">
              <Form.Label>{t("notes")}</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder={t("notesPlaceholder")}
              />
            </Form.Group>

            {/* Past date warning */}
            {isCheckInPast && (
              <Alert variant="warning" className="mb-3">
                {t("checkInPastWarning")}
              </Alert>
            )}

            {/* Overlap warning */}
            {formData.checkInDate && formData.checkOutDate && formData.checkOutDate > formData.checkInDate &&
              guests.some(
                (g) => g.checkInDate < formData.checkOutDate && g.checkOutDate > formData.checkInDate,
              ) && (
              <Alert variant="warning" className="mb-3">
                {t("overlapWarning")}
              </Alert>
            )}

            {/* Limit warning */}
            {limitCheck && limitCheck.wouldExceedLimit && limitCheck.showWarning && (
              <Alert variant="warning" className="mb-3">
                {t("limitWarning", {
                  total: String(limitCheck.totalAfterRegistration),
                  limit: String(limitCheck.monthlyLimit),
                })}
              </Alert>
            )}

            <Button type="submit" className="modal-primary-btn" disabled={isLoading || !!isCheckOutInvalid}>
              {isLoading ? t("loading") : t("registerGuest")}
            </Button>
          </Form>
        </Card.Body>
      </Card>

      {/* Monthly Summary */}
      {monthlySummary && (
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body className="p-4">
            <h5 className="fw-semibold mb-3">{t("monthlySummary")}</h5>
            <div className="d-flex justify-content-between mb-2">
              <span>{t("daysUsedThisMonth")}</span>
              <span className="fw-bold">
                {monthlySummary.totalDaysThisMonth}
                {monthlySummary.monthlyLimit > 0 ? ` / ${monthlySummary.monthlyLimit}` : ""}
              </span>
            </div>
            {monthlySummary.monthlyLimit > 0 && (
              <ProgressBar
                now={limitPercentage}
                variant={limitPercentage >= 100 ? "danger" : limitPercentage >= 80 ? "warning" : "success"}
                label={`${limitPercentage}%`}
              />
            )}
          </Card.Body>
        </Card>
      )}

      {/* Guest History */}
      {historyDisabled ? (
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body className="p-4 text-center text-muted">
            {t("historyDisabledMessage")}
          </Card.Body>
        </Card>
      ) : (
        <Card className="mb-4 border-0 shadow-sm">
          <Card.Body className="p-4">
            <h5 className="fw-semibold mb-3">{t("guestHistory")}</h5>
            {isLoading ? (
              <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : (
              <div className="table-responsive">
                <Table hover className="align-middle">
                  <thead className="table-light">
                    <tr>
                      <th className="fw-semibold text-gray-700">{t("guestFullName")}</th>
                      <th className="fw-semibold text-gray-700">{t("guestIdNumber")}</th>
                      <th className="fw-semibold text-gray-700">{t("checkInDate")}</th>
                      <th className="fw-semibold text-gray-700">{t("checkOutDate")}</th>
                      <th className="fw-semibold text-gray-700">{t("totalDays")}</th>
                      <th className="fw-semibold text-gray-700">{t("notes")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guests.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-4 text-muted">
                          {t("noGuestsFound")}
                        </td>
                      </tr>
                    ) : (
                      guests.map((guest) => (
                        <tr key={guest.id} className="border-bottom">
                          <td className="align-middle">{guest.fullName}</td>
                          <td className="align-middle">{guest.idNumber}</td>
                          <td className="align-middle">
                            {new Date(guest.checkInDate).toLocaleDateString("el-GR")}
                          </td>
                          <td className="align-middle">
                            {new Date(guest.checkOutDate).toLocaleDateString("el-GR")}
                          </td>
                          <td className="align-middle">{guest.totalDays}</td>
                          <td className="align-middle">
                            <div>{guest.notes || "-"}</div>
                            <Button
                              size="sm"
                              variant="link"
                              className="p-0 mt-1 text-muted"
                              onClick={() => navigate(`/dashboard/guests/${guest.id}/thread`)}
                            >
                              <MessageSquare size={14} className="me-1" />
                              <span className="small">
                                {t("thread")} {guest.comments && guest.comments.length > 0 ? `(${guest.comments.length})` : ""}
                              </span>
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      )}
    </Container>
  )
}
